import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronRight, Edit2, Ellipsis, GripVertical, PackagePlus, Plus, Trash } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Category } from '@/interfaces/catalog/Category'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { formatCount } from '@/lib/catalog/catalogTypes'
import { cn } from '@/lib/utils'

interface CategoryTreeProps {
  categories: Category[]
  onEdit: (category: Category) => void
  onDelete: (category: Category) => void
  onAddSubcategory: (category: Category) => void
  /** Zachováno kvůli kompatibilitě, přesun řeší strom sám. */
  onReorder?: (categoryId: string, newParentId: string | null, newPosition: number) => Promise<void>
  onPositionsUpdate?: () => void
  /** Počet produktů s danou hlavní kategorií */
  productCounts?: Map<string, number>
  /** Filtr podle názvu, při hledání je přetahování vypnuté */
  query?: string
  collapsed: Set<string>
  onCollapsedChange: (next: Set<string>) => void
}

type FlatItem = {
  id: string
  parentId: string | null
  depth: number
  category: Category
  childCount: number
  /** isLast[k]: je předek (nebo sama položka) v hloubce k poslední mezi sourozenci */
  isLast: boolean[]
}

const INDENT = 24

const flatten = (
  items: Category[],
  collapsed: Set<string>,
  skipChildrenOf: string | null,
  parentId: string | null = null,
  depth = 0,
  lastPath: boolean[] = [],
): FlatItem[] =>
  items.flatMap((category, index) => {
    const children = category.children ?? []
    const isLast = [...lastPath, index === items.length - 1]
    const item: FlatItem = { id: category.id, parentId, depth, category, childCount: children.length, isLast }
    if (collapsed.has(category.id) || category.id === skipChildrenOf) return [item]
    return [item, ...flatten(children, collapsed, skipChildrenOf, category.id, depth + 1, isLast)]
  })

const findCategory = (items: Category[], id: string): Category | undefined => {
  for (const c of items) {
    if (c.id === id) return c
    const found = findCategory(c.children ?? [], id)
    if (found) return found
  }
}

const countDescendants = (c: Category): number => (c.children ?? []).reduce((acc, ch) => acc + 1 + countDescendants(ch), 0)

/** Odebere kategorii ze stromu a vloží ji pod nového rodiče na daný index. */
const moveInTree = (items: Category[], id: string, parentId: string | null, index: number): Category[] => {
  let moved: Category | undefined
  const remove = (list: Category[]): Category[] =>
    list
      .filter((c) => {
        if (c.id === id) {
          moved = c
          return false
        }
        return true
      })
      .map((c) => ({ ...c, children: remove(c.children ?? []) }))
  const without = remove(items)
  if (!moved) return items
  const node = { ...moved, parentId }
  if (parentId === null) {
    const next = [...without]
    next.splice(index, 0, node)
    return next
  }
  const insert = (list: Category[]): Category[] =>
    list.map((c) => {
      if (c.id === parentId) {
        const children = [...(c.children ?? [])]
        children.splice(index, 0, node)
        return { ...c, children }
      }
      return { ...c, children: insert(c.children ?? []) }
    })
  return insert(without)
}

const getProjection = (items: FlatItem[], activeId: string, overId: string, offsetX: number) => {
  const overIndex = items.findIndex((i) => i.id === overId)
  const activeIndex = items.findIndex((i) => i.id === activeId)
  const activeItem = items[activeIndex]
  const newItems = arrayMove(items, activeIndex, overIndex)
  const previous = newItems[overIndex - 1]
  const next = newItems[overIndex + 1]
  const projected = activeItem.depth + Math.round(offsetX / INDENT)
  const maxDepth = previous ? previous.depth + 1 : 0
  const minDepth = next ? next.depth : 0
  const depth = Math.min(Math.max(projected, minDepth), maxDepth)

  let parentId: string | null = null
  if (depth > 0 && previous) {
    if (depth === previous.depth) parentId = previous.parentId
    else if (depth > previous.depth) parentId = previous.id
    else parentId = newItems.slice(0, overIndex).reverse().find((i) => i.depth === depth)?.parentId ?? null
  }
  return { depth, parentId, newItems, overIndex }
}

const matches = (c: Category, q: string): boolean =>
  c.name.toLowerCase().includes(q) || (c.children ?? []).some((ch) => matches(ch, q))

const filterTree = (items: Category[], q: string): Category[] =>
  items.filter((c) => matches(c, q)).map((c) => ({ ...c, children: filterTree(c.children ?? [], q) }))

export const CategoryTree = ({
  categories,
  onEdit,
  onDelete,
  onAddSubcategory,
  onPositionsUpdate,
  productCounts,
  query = '',
  collapsed,
  onCollapsedChange,
}: CategoryTreeProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'catalog.categories' })
  const [tree, setTree] = useState(categories)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)
  const [offsetX, setOffsetX] = useState(0)

  useEffect(() => setTree(categories), [categories])

  const q = query.trim().toLowerCase()
  const searching = q.length > 0
  const source = useMemo(() => (searching ? filterTree(tree, q) : tree), [tree, q, searching])
  const items = useMemo(() => flatten(source, searching ? new Set() : collapsed, activeId), [source, collapsed, activeId, searching])
  const projection = activeId && overId ? getProjection(items, activeId, overId, offsetX) : null
  const activeItem = activeId ? items.find((i) => i.id === activeId) : undefined

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const toggle = (id: string) => {
    const next = new Set(collapsed)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onCollapsedChange(next)
  }

  const reset = () => {
    setActiveId(null)
    setOverId(null)
    setOffsetX(0)
  }

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(String(active.id))
    setOverId(String(active.id))
  }

  const handleDragMove = ({ delta }: DragMoveEvent) => setOffsetX(delta.x)

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    const proj = projection
    reset()
    if (!over || !proj) return

    const id = String(active.id)
    const moved = findCategory(tree, id)
    if (!moved) return
    const oldParentId = moved.parentId ?? null
    const newParentId = proj.parentId

    // Pořadí mezi sourozenci: viditelný předchůdce se stejným rodičem určí místo vložení.
    const siblingsBefore = proj.newItems.slice(0, proj.overIndex).filter((i) => i.parentId === newParentId && i.id !== id)
    const parentChildren = (newParentId === null ? tree : (findCategory(tree, newParentId)?.children ?? [])).filter((c) => c.id !== id)
    const anchor = siblingsBefore.at(-1)
    const index = anchor ? parentChildren.findIndex((c) => c.id === anchor.id) + 1 : 0

    const currentIndex = (oldParentId === null ? tree : (findCategory(tree, oldParentId)?.children ?? [])).findIndex((c) => c.id === id)
    if (oldParentId === newParentId && currentIndex === index) return

    if (newParentId === null || oldParentId === null) {
      // Backend zatím neumí uložit pořadí hlavních kategorií ani přesun na nejvyšší úroveň (viz docs/redesign/CHANGES.md).
      toast.error('Hlavní úroveň zatím nejde měnit', {
        description: 'Pořadí hlavních kategorií a přesun na nejvyšší úroveň zatím neumíme uložit. Přesouvat jde jen uvnitř kategorií.',
      })
      return
    }

    const previousTree = tree
    const nextTree = moveInTree(tree, id, newParentId, index)
    setTree(nextTree)
    if (newParentId && collapsed.has(newParentId)) {
      const next = new Set(collapsed)
      next.delete(newParentId)
      onCollapsedChange(next)
    }

    try {
      if (oldParentId !== newParentId) {
        await API.patch(`/api/v2/categories/${id}`, { parentId: newParentId })
      }
      const siblings = newParentId === null ? nextTree : (findCategory(nextTree, newParentId)?.children ?? [])
      const positions = Object.fromEntries(siblings.map((c, i) => [c.id, i]))
      await API.post(`/api/v2/category-positions?parentId=${newParentId ?? ''}`, positions)
      toast.success(t('reorder.success'))
      onPositionsUpdate?.()
    } catch (error: unknown) {
      setTree(previousTree)
      toast.error(t('reorder.error'), { description: extractErrorMessage(error) || t('error.description') })
      onPositionsUpdate?.()
    }
  }

  if (searching && items.length === 0) {
    return <p className="px-3 py-8 text-center text-sm text-muted-foreground">Žádná kategorie neodpovídá hledání.</p>
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={({ over }) => setOverId(over ? String(over.id) : null)}
      onDragEnd={handleDragEnd}
      onDragCancel={reset}
    >
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <ul role="tree" aria-label="Kategorie" className="py-1">
          {items.map((item) => (
            <TreeRow
              key={item.id}
              item={item}
              depth={item.id === activeId && projection ? projection.depth : item.depth}
              collapsed={collapsed.has(item.id) && !searching}
              // Hlavní kategorie zatím nepřesouváme: backend neumí vrátit kategorii na nejvyšší úroveň (CHANGES.md).
              dragDisabled={searching || item.depth === 0}
              highlight={searching ? q : ''}
              productCount={productCounts?.get(item.id)}
              showCounts={!!productCounts}
              onToggle={() => toggle(item.id)}
              onEdit={() => onEdit(item.category)}
              onDelete={() => onDelete(item.category)}
              onAddSubcategory={() => onAddSubcategory(item.category)}
            />
          ))}
        </ul>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activeItem && (
          <div className="flex h-10 items-center gap-2 rounded-md border bg-background px-3 text-sm shadow-sm">
            <GripVertical className="size-4 text-muted-foreground" />
            <span className="font-medium">{activeItem.category.name}</span>
            {countDescendants(activeItem.category) > 0 && (
              <span className="rounded-full bg-muted px-2 text-xs text-muted-foreground tabular">
                +{formatCount(countDescendants(activeItem.category), 'podkategorie', 'podkategorie', 'podkategorií')}
              </span>
            )}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}

const Highlight = ({ text, query }: { text: string; query: string }) => {
  if (!query) return <>{text}</>
  const i = text.toLowerCase().indexOf(query)
  if (i < 0) return <>{text}</>
  return (
    <>
      {text.slice(0, i)}
      <mark className="rounded-sm bg-warning-bg px-0.5 text-foreground">{text.slice(i, i + query.length)}</mark>
      {text.slice(i + query.length)}
    </>
  )
}

const TreeRow = ({
  item,
  depth,
  collapsed,
  dragDisabled,
  highlight,
  productCount,
  showCounts,
  onToggle,
  onEdit,
  onDelete,
  onAddSubcategory,
}: {
  item: FlatItem
  depth: number
  collapsed: boolean
  dragDisabled: boolean
  highlight: string
  productCount?: number
  showCounts: boolean
  onToggle: () => void
  onEdit: () => void
  onDelete: () => void
  onAddSubcategory: () => void
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation(undefined, { keyPrefix: 'catalog.categories' })
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: dragDisabled,
  })
  const hasChildren = item.childCount > 0
  const count = productCount ?? 0

  if (isDragging) {
    // Místo, kam kategorie dopadne: linka v cílovém zanoření.
    return (
      <li ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition }} className="relative h-10" aria-hidden>
        <span
          className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center"
          style={{ left: depth * INDENT + 12 }}
        >
          <span className="size-2 rounded-full border-2 border-brand bg-background" />
          <span className="h-0.5 flex-1 rounded-full bg-brand" />
        </span>
      </li>
    )
  }

  return (
    <li
      ref={setNodeRef}
      role="treeitem"
      aria-level={item.depth + 1}
      aria-expanded={hasChildren ? !collapsed : undefined}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className="group/row relative"
    >
      <TreeGuides item={item} />
      <div
        className="flex h-10 items-center gap-1 rounded-md pr-1.5 hover:bg-muted/70 has-[:focus-visible]:bg-muted/70"
        style={{ paddingLeft: depth * INDENT + 32 }}
      >
        <button
          ref={setActivatorNodeRef}
          type="button"
          aria-label={`Přesunout ${item.category.name}`}
          className={cn(
            'absolute top-1/2 left-1 grid size-6 -translate-y-1/2 cursor-grab touch-none place-items-center rounded-sm text-muted-foreground opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100 active:cursor-grabbing',
            dragDisabled && 'invisible',
          )}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" />
        </button>
        {hasChildren ? (
          <button
            type="button"
            onClick={onToggle}
            aria-label={collapsed ? 'Rozbalit' : 'Sbalit'}
            className="grid size-6 shrink-0 place-items-center rounded-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronRight className={cn('size-4 transition-transform duration-150', !collapsed && 'rotate-90')} />
          </button>
        ) : (
          <span className="size-6 shrink-0" />
        )}
        <button type="button" onClick={onEdit} className="min-w-0 truncate rounded-sm px-1 text-left text-sm hover:underline">
          <Highlight text={item.category.name} query={highlight} />
        </button>
        {hasChildren && collapsed && (
          <span className="shrink-0 rounded-full bg-muted px-1.5 text-[11px] text-muted-foreground tabular">{item.childCount}</span>
        )}

        <span className="ml-auto flex shrink-0 items-center gap-0.5 pl-3">
          {showCounts && (count > 0 || !hasChildren) && (
            <Link
              to={`/catalog/products?mainCategoryId=${item.id}`}
              className={cn(
                'tabular rounded-sm px-1.5 text-xs hover:text-foreground hover:underline',
                count > 0 ? 'text-muted-foreground' : 'text-muted-foreground/70',
              )}
            >
              {count === 0 ? 'bez produktů' : formatCount(count, 'produkt', 'produkty', 'produktů')}
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('actions.addSubcategory')}
            title={t('actions.addSubcategory')}
            className="opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100"
            onClick={onAddSubcategory}
          >
            <Plus />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Akce kategorie">
                <Ellipsis />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Edit2 />
                {t('actions.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onAddSubcategory}>
                <Plus />
                {t('actions.addSubcategory')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/catalog/product/new?categoryId=${item.id}`)}>
                <PackagePlus />
                {t('actions.addProduct')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={onDelete}>
                <Trash />
                {t('actions.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      </div>
    </li>
  )
}

/** Spojnice stromu: svislá linka pod šipkou rodiče a zaoblené odbočení k položce. */
const GUIDE_X = 32 + 12 // místo pro úchyt + střed šipky

const TreeGuides = ({ item }: { item: FlatItem }) => {
  if (item.depth === 0) return null
  const own = item.depth - 1
  const isLast = item.isLast[item.depth]
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0">
      {Array.from({ length: own }).map((_, level) =>
        item.isLast[level + 1] ? null : (
          <span key={level} className="absolute inset-y-0 w-px bg-foreground/15" style={{ left: level * INDENT + GUIDE_X }} />
        ),
      )}
      {isLast ? (
        <span
          className="absolute top-0 h-1/2 w-6 rounded-bl-lg border-b border-l border-foreground/15"
          style={{ left: own * INDENT + GUIDE_X }}
        />
      ) : (
        <>
          <span className="absolute inset-y-0 w-px bg-foreground/15" style={{ left: own * INDENT + GUIDE_X }} />
          <span className="absolute top-1/2 h-px w-6 bg-foreground/15" style={{ left: own * INDENT + GUIDE_X }} />
        </>
      )}
    </span>
  )
}
